/**
 * Fallback agent registry — the ambient population shown when the
 * Genesis-Live observer bridge is unreachable (freshness OFFLINE).
 *
 * Honesty contract: these are simulated environmental entities whose roles
 * mirror the real working structure of the laboratory (research, knowledge,
 * infrastructure, review...); the UI labels them as a simulation and never
 * presents them as live LLM activity. When the observer bridge is reachable,
 * the site shows the real NanoAgent population (chain-verified against the
 * running universe, `src/lab/live-fold.ts`) instead of this registry — see
 * `src/lab/observed-state.ts` / `src/lab/live-poller.ts`.
 */

import type { LabAgent } from '../lab/types';

export const fallbackAgents: LabAgent[] = [
  {
    id: 'r-01',
    name: 'R-01',
    kind: 'research',
    role: 'Research Intelligence',
    task: 'Reviewing the agent-native software direction',
    attachedTo: 'agent-native-software',
    status: 'active',
    activity: 0.8,
    connections: ['k-01', 'v-01'],
  },
  {
    id: 'r-02',
    name: 'R-02',
    kind: 'research',
    role: 'Research Intelligence',
    task: 'Surveying mastery-model literature',
    attachedTo: 'adaptive-mastery',
    status: 'active',
    activity: 0.65,
    connections: ['k-02'],
  },
  {
    id: 'r-03',
    name: 'R-03',
    kind: 'research',
    role: 'Research Intelligence',
    task: 'Stress-testing numerical edge cases',
    attachedTo: 'compensated-numerics',
    status: 'moving',
    activity: 0.5,
    connections: ['e-01'],
  },
  {
    id: 'r-04',
    name: 'R-04',
    kind: 'research',
    role: 'Research Intelligence',
    task: 'Formulating hypotheses on structural growth',
    attachedTo: 'grown-networks',
    status: 'idle',
    activity: 0.3,
    connections: ['v-01'],
  },
  {
    id: 'k-01',
    name: 'K-01',
    kind: 'knowledge',
    role: 'Knowledge Engineering',
    task: 'Linking new nodes into the laboratory graph',
    attachedTo: 'knowledge-graphs',
    status: 'active',
    activity: 0.9,
    connections: ['r-01', 'k-02'],
  },
  {
    id: 'k-02',
    name: 'K-02',
    kind: 'knowledge',
    role: 'Knowledge Engineering',
    task: 'Tracking provenance of imported material',
    attachedTo: 'knowledge-graphs',
    status: 'active',
    activity: 0.55,
    connections: ['k-01', 'r-02'],
  },
  {
    id: 'a-01',
    name: 'A-01',
    kind: 'architecture',
    role: 'Architecture',
    task: 'Rebalancing service boundaries',
    attachedTo: 'agent-orchestration',
    status: 'active',
    activity: 0.6,
    connections: ['i-01', 's-01'],
  },
  {
    id: 's-01',
    name: 'S-01',
    kind: 'security',
    role: 'Security',
    task: 'Sweeping the infrastructure contour',
    status: 'moving',
    activity: 0.45,
    connections: ['i-01'],
  },
  {
    id: 's-02',
    name: 'S-02',
    kind: 'security',
    role: 'Security',
    task: 'Auditing publication boundaries',
    status: 'idle',
    activity: 0.25,
    connections: ['v-01'],
  },
  {
    id: 'e-01',
    name: 'E-01',
    kind: 'experiment',
    role: 'Experimentation',
    task: 'Running consensus failure-injection scenarios',
    attachedTo: 'multi-agent-consensus',
    status: 'active',
    activity: 0.85,
    connections: ['r-03', 'd-01'],
  },
  {
    id: 'i-01',
    name: 'I-01',
    kind: 'infrastructure',
    role: 'Infrastructure',
    task: 'Watching deployment health',
    status: 'active',
    activity: 0.5,
    connections: ['a-01', 's-01'],
  },
  {
    id: 'p-01',
    name: 'P-01',
    kind: 'product',
    role: 'Product',
    task: 'Reconciling research findings with product signals',
    attachedTo: 'studyninja',
    status: 'active',
    activity: 0.6,
    connections: ['r-02', 'd-01'],
  },
  {
    id: 'd-01',
    name: 'D-01',
    kind: 'data',
    role: 'Data',
    task: 'Curating evaluation datasets',
    status: 'idle',
    activity: 0.35,
    connections: ['e-01', 'p-01'],
  },
  {
    id: 'v-01',
    name: 'V-01',
    kind: 'review',
    role: 'Scientific Review',
    task: 'Reviewing claims before they leave the laboratory',
    attachedTo: 'unified-wholeness',
    status: 'active',
    activity: 0.7,
    connections: ['r-01', 'r-04', 's-02'],
  },
];

export const agentById = new Map(fallbackAgents.map((a) => [a.id, a]));
export const isAgentId = (s: string) => agentById.has(s);

export const agentKindCounts = fallbackAgents.reduce<Record<string, number>>((acc, a) => {
  acc[a.kind] = (acc[a.kind] ?? 0) + 1;
  return acc;
}, {});
